import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { createWorker } from 'tesseract.js';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Camera, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

interface VinScannerProps {
  onScanSuccess: (vin: string) => void;
}

export const VinScanner = ({ onScanSuccess }: VinScannerProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const ocrIntervalRef = useRef<number | null>(null);

  const extractVinFromText = (text: string): string | null => {
    // Normalize text: uppercase, remove spaces/dashes, fix common OCR confusions
    const normalized = text
      .toUpperCase()
      .replace(/[\s-]/g, '')
      .replace(/O/g, '0')
      .replace(/I/g, '1')
      .replace(/Q/g, '0');

    // Try to find a VIN-like pattern (10-20 characters) to be more tolerant
    const vinPattern = /[A-Z0-9]{10,20}/g;
    const matches = normalized.match(vinPattern);
    return matches ? matches[0] : null;
  };

  const performOCR = async (videoElement: HTMLVideoElement) => {
    if (ocrProcessing) return;
    
    setOcrProcessing(true);
    try {
      // Capture current video frame
      const canvas = document.createElement('canvas');
      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      
      ctx.drawImage(videoElement, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);

      // Perform OCR
      const worker = await createWorker('eng', 1, {
        workerPath: 'https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/worker.min.js',
        langPath: 'https://tessdata.projectnaptha.com/4.0.0',
        corePath: 'https://cdn.jsdelivr.net/npm/tesseract.js-core@5/tesseract-core-simd.wasm.js',
      });
      
      const { data: { text } } = await worker.recognize(imageData);
      await worker.terminate();

      // Look for VIN in recognized text
      const vin = extractVinFromText(text);
      if (vin) {
        onScanSuccess(vin);
        stopScanner();
        setIsOpen(false);
        toast.success(t('vinScanner.success.recognized'));
      }
    } catch (error) {
      console.error('OCR error:', error);
    } finally {
      setOcrProcessing(false);
    }
  };

  const startScanner = async () => {
    try {
      setCameraError(null);
      const html5QrCode = new Html5Qrcode("vin-scanner");
      scannerRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 300, height: 150 },
          aspectRatio: 2.0,
        },
        (decodedText) => {
          // Try barcode first
          const vinMatch = decodedText.match(/[A-HJ-NPR-Z0-9]{17}/i);
          if (vinMatch) {
            onScanSuccess(vinMatch[0].toUpperCase());
            stopScanner();
            setIsOpen(false);
            toast.success(t('vinScanner.success.scanned'));
          }
        },
        undefined
      );
      setIsScanning(true);

      // Get video element and start OCR interval
      const videoElement = document.querySelector('#vin-scanner video') as HTMLVideoElement;
      if (videoElement) {
        videoRef.current = videoElement;
        // Run OCR every 2 seconds
        ocrIntervalRef.current = window.setInterval(() => {
          performOCR(videoElement);
        }, 2000);
      }
    } catch (err: any) {
      console.error("Error starting scanner:", err);
      setCameraError(err.message || t('vinScanner.errors.accessDenied'));
      toast.error(t('vinScanner.errors.accessDenied'));
    }
  };

  const stopScanner = async () => {
    // Clear OCR interval
    if (ocrIntervalRef.current) {
      clearInterval(ocrIntervalRef.current);
      ocrIntervalRef.current = null;
    }

    if (scannerRef.current && isScanning) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
        scannerRef.current = null;
        setIsScanning(false);
        setOcrProcessing(false);
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      stopScanner();
    }
  };

  useEffect(() => {
    if (isOpen && !isScanning) {
      // Add delay to ensure DOM element is mounted
      const timer = setTimeout(() => {
        startScanner();
      }, 100);
      return () => {
        clearTimeout(timer);
        stopScanner();
      };
    }
    
    return () => {
      stopScanner();
    };
  }, [isOpen]);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={() => setIsOpen(true)}
        className="shrink-0"
      >
        <Camera className="h-4 w-4" />
      </Button>

      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t('vinScanner.title')}</DialogTitle>
            <DialogDescription>
              {t('vinScanner.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
              <div id="vin-scanner" className="w-full h-full" />
              {ocrProcessing && (
                <div className="absolute top-4 right-4 bg-primary/90 text-primary-foreground px-3 py-2 rounded-lg flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">{t('vinScanner.analyzing')}</span>
                </div>
              )}
              {cameraError && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/80 text-white p-4 text-center">
                  <div>
                    <p className="font-semibold mb-2">{t('vinScanner.cameraError')}</p>
                    <p className="text-sm">{cameraError}</p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                <X className="mr-2 h-4 w-4" />
                {t('vinScanner.cancel')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
